import React, { useState, useEffect, useCallback, Suspense } from 'react';
import {
  Box,
  Button,
  ButtonBase,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
  useMediaQuery,
} from '@mui/material';
import {
  PhoneIphone as PhoneIcon,
  DesktopMac as DesktopIcon,
  AdminPanelSettings as AdminIcon,
  Route as RouteIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { enableDemoApi, disableDemoApi } from './lib/demoApi';

const DEMO_PREV_LANG_KEY = 'demo_prev_ui_lang';

const DemoController = React.lazy(() => import('./DemoController'));

const DEMO_STATE_KEY = 'demo_controller_state';
const DEMO_LANG_KEY = 'demo_lang';
const DEMO_PART_KEY = 'demo_part';

const DEMO_OPTIONS = [
  { part: 'crew', lang: 'en', partKey: 'demoPicker.crew', langKey: 'demoPicker.english', langCode: 'EN' },
  { part: 'crew', lang: 'es', partKey: 'demoPicker.crew', langKey: 'demoPicker.spanish', langCode: 'ES' },
  { part: 'user', lang: 'en', partKey: 'demoPicker.user', langKey: 'demoPicker.english', langCode: 'EN' },
  { part: 'user', lang: 'es', partKey: 'demoPicker.user', langKey: 'demoPicker.spanish', langCode: 'ES' },
  { part: 'admin', lang: 'en', partKey: 'demoPicker.admin', langKey: 'demoPicker.english', langCode: 'EN' },
  { part: 'admin', lang: 'es', partKey: 'demoPicker.admin', langKey: 'demoPicker.spanish', langCode: 'ES' },
  { part: 'routes', lang: 'en', partKey: 'demoPicker.routes', langKey: 'demoPicker.english', langCode: 'EN' },
  { part: 'routes', lang: 'es', partKey: 'demoPicker.routes', langKey: 'demoPicker.spanish', langCode: 'ES' },
];

/**
 * Paramount demo launcher. Invisible until the user presses a keyboard
 * shortcut:
 *   Ctrl+Alt+D  →  open the walkthrough picker
 *
 * On activation the launcher enables the demo API layer, which swaps the
 * real backend for deterministic fixtures so every playthrough shows the
 * same customers, the same stops, and the same clock-in state. When the
 * demo ends (naturally, via Escape, or page unload) the real fetch is
 * restored so the app goes back to live data.
 *
 * The controller itself is lazy-loaded so regular users pay zero bundle
 * cost for the demo runtime.
 */
const DemoLauncher = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [active, setActive] = useState(() => {
    try {
      const raw = sessionStorage.getItem(DEMO_STATE_KEY);
      if (raw) {
        const state = JSON.parse(raw);
        return state && state.active === true;
      }
    } catch {
      /* ignore */
    }
    return false;
  });
  const [panelOpen, setPanelOpen] = useState(false);

  const launch = useCallback(async ({ part, lang }) => {
    try {
      sessionStorage.setItem(DEMO_LANG_KEY, lang);
      sessionStorage.setItem(DEMO_PART_KEY, part);
    } catch { /* ignore */ }

    try {
      await enableDemoApi();
    } catch (err) {
      console.error('demoApi: failed to enable', err);
      // Refuse to launch without fixtures — better to fail loudly than
      // show real customer data in a recorded demo.
      return;
    }

    // Swap the UI language to match the narration so Spanish voice never
    // talks over English buttons (or vice versa). The previous language
    // is stashed in sessionStorage so we can restore it when the demo ends.
    try {
      const previous = i18n.language || 'en';
      sessionStorage.setItem(DEMO_PREV_LANG_KEY, previous);
      const targetLang = lang === 'es' ? 'es-MX' : 'en';
      if (previous !== targetLang) {
        await i18n.changeLanguage(targetLang);
      }
    } catch (err) {
      console.warn('demo: could not switch UI language', err);
    }

    setPanelOpen(false);
    setActive(true);
  }, [i18n]);

  const handleComplete = useCallback(() => {
    setActive(false);
    disableDemoApi();

    // Restore whatever UI language the user had before launching the demo.
    try {
      const previous = sessionStorage.getItem(DEMO_PREV_LANG_KEY);
      if (previous && previous !== i18n.language) {
        i18n.changeLanguage(previous).catch(() => { /* ignore */ });
      }
      sessionStorage.removeItem(DEMO_PREV_LANG_KEY);
    } catch { /* ignore */ }

    try {
      sessionStorage.removeItem(DEMO_LANG_KEY);
      sessionStorage.removeItem(DEMO_PART_KEY);
    } catch { /* ignore */ }
  }, [i18n]);

  useEffect(() => {
    const handler = (e) => {
      if (!e.ctrlKey || !e.altKey) return;
      const key = typeof e.key === 'string' ? e.key.toLowerCase() : '';

      if (key === 'd') {
        e.preventDefault();
        if (!active) {
          setPanelOpen(true);
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [active]);

  // If the page is resuming a mid-demo reload (sessionStorage says a run
  // is in progress), re-enable the demo API so the resumed scene still
  // sees fixtures instead of the real backend.
  useEffect(() => {
    if (active) {
      enableDemoApi().catch((err) => console.error('demoApi: resume failed', err));
    }
    return () => {
      // Cleanup if the component ever unmounts (shouldn't in practice).
      if (active) disableDemoApi();
    };
  }, [active]);

  if (!active) {
    return (
      <Dialog
        open={panelOpen}
        onClose={() => setPanelOpen(false)}
        aria-labelledby="demo-picker-title"
        fullScreen={isMobile}
        fullWidth
        maxWidth="xs"
        sx={{ zIndex: 2147483646 }}
        PaperProps={{
          sx: {
            borderRadius: { xs: 0, sm: 3 },
            m: { xs: 0, sm: 2 },
          },
        }}
      >
        <DialogTitle
          id="demo-picker-title"
          sx={{
            color: '#000062',
            fontSize: { xs: '1.5rem', sm: '1.25rem' },
            fontWeight: 700,
            pt: { xs: 3, sm: 2 },
            pb: 1,
          }}
        >
          {t('demoPicker.title')}
        </DialogTitle>
        <DialogContent dividers>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ mb: 2.5, fontSize: { xs: '1rem', sm: '0.875rem' } }}
          >
            {t('demoPicker.subtitle')}
          </Typography>
          <Stack spacing={1.5}>
            {DEMO_OPTIONS.map((option) => {
              const partLabel = t(option.partKey);
              const langLabel = t(option.langKey);
              const ariaLabel = t('demoPicker.startAria', {
                label: `${partLabel} — ${langLabel}`,
              });
              const Icon = option.part === 'crew'
                ? PhoneIcon
                : option.part === 'user'
                  ? DesktopIcon
                  : option.part === 'routes'
                    ? RouteIcon
                    : AdminIcon;
              const iconBg = option.part === 'crew'
                ? '#de6127'
                : option.part === 'user'
                  ? '#000062'
                  : option.part === 'routes'
                    ? '#0277bd'
                    : '#1b5e20';
              return (
                <ButtonBase
                  key={`${option.part}-${option.lang}`}
                  onClick={() => launch(option)}
                  aria-label={ariaLabel}
                  focusRipple
                  sx={{
                    width: '100%',
                    minHeight: 72,
                    borderRadius: 2,
                    px: 2,
                    py: 1.25,
                    border: '2px solid #000062',
                    bgcolor: '#fff',
                    color: '#000062',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    transition: 'background-color 120ms ease-in-out',
                    '&:hover': { bgcolor: 'rgba(0, 0, 98, 0.06)' },
                    '&:focus-visible': {
                      outline: '3px solid #de6127',
                      outlineOffset: '2px',
                    },
                  }}
                >
                  <Box
                    aria-hidden="true"
                    sx={{
                      width: 44,
                      height: 44,
                      flexShrink: 0,
                      borderRadius: '50%',
                      bgcolor: iconBg,
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Icon sx={{ fontSize: 24 }} />
                  </Box>
                  <Box
                    sx={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      minWidth: 0,
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      sx={{
                        color: '#000062',
                        fontWeight: 700,
                        lineHeight: 1.2,
                        fontSize: { xs: '1.05rem', sm: '1rem' },
                      }}
                    >
                      {partLabel}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'text.secondary',
                        fontWeight: 500,
                        fontSize: { xs: '0.9rem', sm: '0.85rem' },
                      }}
                    >
                      {langLabel}
                    </Typography>
                  </Box>
                  <Chip
                    label={option.langCode}
                    size="small"
                    sx={{
                      flexShrink: 0,
                      bgcolor: '#000062',
                      color: '#fff',
                      fontWeight: 700,
                      letterSpacing: 0.5,
                    }}
                  />
                </ButtonBase>
              );
            })}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: { xs: 2.5, sm: 3 }, pb: { xs: 3, sm: 2 } }}>
          <Button
            onClick={() => setPanelOpen(false)}
            aria-label={t('demoPicker.closeAria')}
            fullWidth={isMobile}
            variant="outlined"
            sx={{
              minHeight: { xs: 52, sm: 40 },
              borderColor: '#000062',
              color: '#000062',
              fontWeight: 600,
              textTransform: 'none',
              '&:hover': { borderColor: '#000043', bgcolor: 'rgba(0,0,98,0.04)' },
            }}
          >
            {t('common.cancel')}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Box>
      <Suspense fallback={null}>
        <DemoController onComplete={handleComplete} />
      </Suspense>
    </Box>
  );
};

export default DemoLauncher;
